import { MouseEvent, ReactElement, useCallback, useMemo } from 'react'
import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import {
  remapPlanTablePositionForAnchorChange,
  type PlanTablePositionAnchor,
} from 'src/domain/planTables/positionAnchor'
import {
  computeTableLayoutDimensions,
  type TableLayoutMetrics,
} from 'src/domain/planTables/tableLayout'
import type { TableData } from 'src/domain/planTables/types'

const ANCHOR_OPTIONS: { value: PlanTablePositionAnchor; label: string }[] = [
  { value: 'top-left', label: 'Oben links' },
  { value: 'top-right', label: 'Oben rechts' },
  { value: 'bottom-left', label: 'Unten links' },
  { value: 'bottom-right', label: 'Unten rechts' },
]

interface Props {
  position: { x: number; y: number; z: number }
  positionAnchor: PlanTablePositionAnchor
  /** The table content the plan draws (for template tables: as resolved for the previewed plan). */
  table: TableData
  /**
   * Passed in rather than read from `usePlanTableLayoutMetrics` here: the in-scene edit popup
   * renders this inside a drei `<Html>`, which mounts its own React root, so the QueryClient (and
   * every other context) is out of reach. Callers resolve it where the context exists.
   */
  layoutMetrics: TableLayoutMetrics
  onChange: (changes: {
    position: { x: number; y: number; z: number }
    positionAnchor: PlanTablePositionAnchor
  }) => void
}

/**
 * Four-corner position-anchor control. Switching anchor remaps `position` so the
 * visual table rect stays fixed.
 */
const PlanTablePositionAnchorFields = ({
  position,
  positionAnchor,
  table,
  layoutMetrics,
  onChange,
}: Props): ReactElement => {
  const size = useMemo(
    () => computeTableLayoutDimensions(table, layoutMetrics),
    [layoutMetrics, table],
  )

  const handleChange = useCallback(
    (_event: MouseEvent<HTMLElement>, nextAnchor: PlanTablePositionAnchor | null) => {
      if (!nextAnchor || nextAnchor === positionAnchor) return
      const nextPosition2d = remapPlanTablePositionForAnchorChange(
        position,
        { width: size.totalWidth, height: size.totalHeight },
        positionAnchor,
        nextAnchor,
      )
      onChange({
        positionAnchor: nextAnchor,
        position: { ...position, x: nextPosition2d.x, y: nextPosition2d.y },
      })
    },
    [onChange, position, positionAnchor, size.totalHeight, size.totalWidth],
  )

  return (
    <>
      <Typography variant="caption" color="text.secondary">
        Positionsanker
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={positionAnchor}
        onChange={handleChange}
        fullWidth
        data-cy="plan-table-position-anchor"
      >
        {ANCHOR_OPTIONS.map(option => (
          <ToggleButton
            key={option.value}
            value={option.value}
            data-cy={`plan-table-position-anchor-${option.value}`}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </>
  )
}

export default PlanTablePositionAnchorFields
